import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import type {
  TenantSelectionResult,
  TenantSelectorRowData,
} from '../../../components/tenant-selector/types';
import { useTenantSelectorRowData } from '../../../components/tenant-selector/useTenantSelectorRowData';
import type { TenantSelection } from '../automations-tab/templates.types';
import { templateKeys } from '../template-query-keys';
import { useAutomationStatuses, useTemplateSyncStatusPolling } from '../useAutomationTemplates';
import {
  automationStatusesToIndicator,
  automationStatusToTenantLabel,
  SYNCED_AND_ON_LABEL,
} from './automationTemplateTenantStatus';
import { useTenantSegmentConflicts } from './useTenantSegmentConflicts';

export function useAutomationTemplateTenantSelection(options: {
  templateId: string;
  tenantSelection: TenantSelection;
}) {
  const { templateId, tenantSelection } = options;
  const queryClient = useQueryClient();

  const { data: templateSyncData, syncingTenantIds } = useTemplateSyncStatusPolling(templateId);

  const jobSyncActive = Boolean(templateSyncData?.hasActiveJobs);

  const { data: automationStatuses, isLoading: isLoadingStatuses } = useAutomationStatuses(templateId, {
    refetchInterval: jobSyncActive ? 3000 : false,
  });

  // Include templateId so switching templates does not compare against stale scope from another template.
  const syncStateKey = `${templateId}:${jobSyncActive}:${syncingTenantIds.slice().sort().join(',')}`;
  const [previousSyncStateKey, setPreviousSyncStateKey] = useState(syncStateKey);

  useEffect(() => {
    if (previousSyncStateKey === syncStateKey) {
      return;
    }
    if (previousSyncStateKey !== '') {
      void queryClient.invalidateQueries({
        queryKey: templateKeys.automationStatuses(templateId),
      });
    }
    setPreviousSyncStateKey(syncStateKey);
  }, [previousSyncStateKey, queryClient, syncStateKey, templateId]);

  // The dialog shows the notice for any mode; here validation only gates saving a selection.
  const { conflicts, isValidating } = useTenantSegmentConflicts(templateId, tenantSelection, {
    enabled: tenantSelection.selectionType === 'selected',
  });

  const rowDataByTenantId = useMemo(() => {
    const syncingSet = new Set(syncingTenantIds);
    const map: Record<string, Partial<TenantSelectorRowData>> = {};

    if (!isLoadingStatuses && automationStatuses?.businessStatuses) {
      for (const row of automationStatuses.businessStatuses) {
        const label =
          row.automations.length === 0
            ? 'Not synced'
            : [...new Set(row.automations.map(a => automationStatusToTenantLabel(a.status)))]
                .sort()
                .join(', ');
        const hasAutomationOn = label.includes(SYNCED_AND_ON_LABEL);
        map[row.businessId] = {
          statusIndicator: {
            label,
            indicator: automationStatusesToIndicator(row.automations.map(a => a.status)),
            lockReason: hasAutomationOn
              ? 'This tenant has an active automation. Disable the automation for this tenant before removing it.'
              : undefined,
          },
          isActive: hasAutomationOn,
          isSyncing: syncingSet.has(row.businessId),
        };
      }
    }

    for (const id of syncingTenantIds) {
      if (map[id] == null) {
        map[id] = { isSyncing: true };
      }
    }

    return map;
  }, [isLoadingStatuses, automationStatuses, syncingTenantIds]);

  const { tenantRowData, isLoading } = useTenantSelectorRowData(rowDataByTenantId);

  return {
    tenantRowData,
    isLoading,
    conflicts,
    isValidating,
    syncingTenantCount: syncingTenantIds.length,
    jobSyncActive,
  } satisfies TenantSelectionResult;
}
